import React from 'react';
import { Globe } from 'lucide-react';
import { SOCIAL_NETWORKS, socialEntries } from '../utils/organizationSocials';

/**
 * An organisation's banner, logo, name and social links.
 *
 * A COMPONENT, not markup inside the page, because Phase 6 renders the same
 * organisation publicly at /orgs/:slug. Building it twice is how the in-app
 * page and the public one end up with two opinions about what an organisation
 * looks like — and the public one is the copy nobody notices has drifted.
 *
 * `editable` is presentation only. It shows the upload affordances; it grants
 * nothing. Every write goes through the API, which checks the caller's role
 * regardless of what this component was told.
 */
export default function OrganizationHeader({
  organization,
  editable = false,
  onEditBanner,
  onEditLogo,
  compact = false,
}) {
  const org = organization || {};
  const socials = socialEntries(org.socials);

  return (
    <div className={`org-header${compact ? ' org-header--compact' : ''}`}>
      <div
        className="org-header-banner"
        style={org.banner_url ? { backgroundImage: `url(${org.banner_url})` } : undefined}
      >
        {editable && (
          <button type="button" className="org-header-banner-edit" onClick={onEditBanner}>
            Change banner
          </button>
        )}
      </div>

      <div className="org-header-body">
        {/* Overlaps the banner — the logo belongs to both bands, which is what
            makes the pair read as one header rather than two stacked blocks. */}
        <div className="org-header-logo-wrap">
          {org.logo_url ? (
            <img className="org-header-logo" src={org.logo_url} alt="" />
          ) : (
            <div className="org-header-logo org-header-logo--empty" aria-hidden>
              {(org.name || '?').trim().charAt(0).toUpperCase()}
            </div>
          )}
          {editable && (
            <button type="button" className="org-header-logo-edit" onClick={onEditLogo}>
              Change
            </button>
          )}
        </div>

        <div className="org-header-texts">
          <h2 className="org-header-name">{org.name || 'Untitled organization'}</h2>
          {org.description && <p className="org-header-desc">{org.description}</p>}

          <div className="org-header-links">
            {org.website_url && (
              <a
                className="org-header-link"
                href={org.website_url}
                target="_blank"
                // noopener/noreferrer on every outbound link: these URLs are
                // user-supplied, and without it the destination gets a handle
                // on this window through window.opener.
                rel="noopener noreferrer"
              >
                <Globe size={13} aria-hidden />
                {org.website_url.replace(/^https?:\/\//, '')}
              </a>
            )}
            {socials.map(({ key, url }) => (
              <a
                key={key}
                className="org-header-link"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                title={SOCIAL_NETWORKS[key]?.label || key}
              >
                {SOCIAL_NETWORKS[key]?.label || key}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
